import { colors } from '@dexma/ui-components';
import i18n from 'asset/translation';
import Branding from 'domain/entity/branding';
import { Period, PeriodType } from 'domain/entity/widgetData';
import { WidgetGaugeComparison, WidgetGaugeIndicator, WidgetGaugeRange } from 'domain/entity/widgets/GaugeData';
import { applyTwoDecimals } from 'adapter/in/common/utils/numberFormatter';
import { defaultDateFormatter, getColor } from 'adapter/in/common/utils/widgetDataUtils';

const useGaugeWidget = (
    branding: Branding,
    dateFormat: string,
    decimalSeparator: string,
    thousandSeparator: string
): {
    parseGaugeIndicator: (
        indicator: WidgetGaugeIndicator,
        title: string,
        units: string,
        period?: Period
    ) => { color: string; tooltip: string; value: number };
    parseGaugeRange: (range: WidgetGaugeRange, min: number, max: number) => { color: string; tooltip: string; from: number; to: number };
    getComparisonText: (comparison: WidgetGaugeComparison) => string;
    parseComparisonIndicator: (
        comparison: WidgetGaugeComparison,
        title: string,
        units: string
    ) => { color: string; tooltip: string; value: number };
} => {
    const parseGaugeIndicator = (indicator: WidgetGaugeIndicator, title: string, units: string, period?: Period) => {
        let periodFormatted;
        if (period) {
            periodFormatted =
                period.type === PeriodType.INTERVAL && period.from && period.to
                    ? ` ${i18n.t('common.for')} ${defaultDateFormatter(period.from, dateFormat.toUpperCase())} - ${defaultDateFormatter(
                          period.to,
                          dateFormat.toUpperCase()
                      )}`
                    : ` ${i18n.t('common.for')} ${period.text?.toLowerCase()}`;
        } else {
            periodFormatted = '';
        }

        return {
            color: getColor(branding)(indicator.color),
            tooltip: `${title}${periodFormatted}: ${applyTwoDecimals(
                String(indicator.value),
                decimalSeparator,
                thousandSeparator
            )} ${units}`,
            value: indicator.value,
        };
    };

    const parseInsideRange = (value: number, startRange: number, endRange: number): number => {
        if (value > endRange) {
            return endRange;
        }
        if (value < startRange) {
            return startRange;
        }
        return value;
    };

    const parseGaugeRange = (range: WidgetGaugeRange, min: number, max: number) => ({
        color: getColor(branding)(range.color),
        from: parseInsideRange(range.from, min, max),
        to: parseInsideRange(range.to, min, max),
        tooltip: `${range.name}: ${applyTwoDecimals(String(range.from), decimalSeparator, thousandSeparator)} - ${applyTwoDecimals(
            String(range.to),
            decimalSeparator,
            thousandSeparator
        )}`,
    });

    const getComparisonText = (comparison: WidgetGaugeComparison) =>
        comparison.period.type === PeriodType.INTERVAL && comparison.period.from && comparison.period.to
            ? `${i18n.t('common.comparedTo')} ${defaultDateFormatter(
                  comparison.period.from,
                  dateFormat.toUpperCase()
              )} - ${defaultDateFormatter(comparison.period.to, dateFormat.toUpperCase())}`
            : `${i18n.t('common.comparedTo')} ${comparison.period.text?.toLowerCase()}`;

    const parseComparisonIndicator = (comparison: WidgetGaugeComparison, title: string, units: string) => ({
        color: colors.black,
        tooltip: `${i18n.t('common.compared')} ${title} ${i18n.t('common.for')} ${
            comparison.period.type === PeriodType.INTERVAL && comparison.period.from && comparison.period.to
                ? `${defaultDateFormatter(comparison.period.from, dateFormat.toUpperCase())} - ${defaultDateFormatter(
                      comparison.period.to,
                      dateFormat.toUpperCase()
                  )}`
                : comparison.period.text?.toLowerCase()
        }: ${comparison.value ? applyTwoDecimals(comparison.value.toString(), decimalSeparator, thousandSeparator) : '-'} ${units}`,
        value: Number(comparison.value),
    });

    return {
        parseGaugeIndicator,
        parseGaugeRange,
        getComparisonText,
        parseComparisonIndicator,
    };
};

export default useGaugeWidget;
